/**
 * Слой доступа к данным. Две реализации одного интерфейса:
 *   • ApiRepo   — REST /api/* поверх Postgres на сервере (рабочий режим: встройка
 *                 в Битрикс / сервер раздаёт SPA — /api тот же origin).
 *   • DexieRepo — локальный IndexedDB + демо-сид (standalone/локальная разработка
 *                 без backend).
 *
 * Режим выбирается при старте пробой GET /api/ping. Стор работает с выбранным
 * репозиторием, не зная, какой именно.
 */
import { db, DEFAULT_SETTINGS, ensureSettings } from '@/db';
import { seedIfEmpty } from '@/db/seed';
import { authHeaders, notifyUnauthorized } from '@/auth/session';
import { collectAttachmentBodies, stripSavedAttachmentBodies } from '@/utils/attachments';
import type {
  AppSettings, Branch, Deal, Employee, GameFormat, Occasion, RefusalReason, Source, Team,
} from '@/types';

/** Справочники без сделок — быстрая часть загрузки (фаза 1 стартового экрана). */
export interface RefSnapshot {
  branches: Branch[];
  employees: Employee[];
  teams: Team[];
  sources: Source[];
  occasions: Occasion[];
  game_formats: GameFormat[];
  refusal_reasons: RefusalReason[];
}

export interface Snapshot extends RefSnapshot {
  deals: Deal[];
}

export interface Repo {
  readonly mode: 'api' | 'local';
  loadAll(): Promise<Snapshot>;
  /** Только справочники (без сделок) — быстрая фаза 1 стартовой загрузки. */
  loadRefs(): Promise<RefSnapshot>;
  /**
   * Сделки за период [from; to) по дате игры (epoch-ms). Без параметров — все сделки.
   * Используется для двухфазной загрузки: сначала текущий месяц, потом в фоне всё.
   */
  loadDeals(from?: number, to?: number): Promise<Deal[]>;
  getSettings(): Promise<AppSettings>;
  putSettings(s: AppSettings): Promise<void>;
  putBranch(b: Branch): Promise<void>;
  deleteBranch(id: string): Promise<void>;
  putEmployee(e: Employee): Promise<void>;
  deleteEmployee(id: string): Promise<void>;
  putTeam(t: Team): Promise<void>;
  deleteTeam(id: string): Promise<void>;
  /** Сохраняет сделку и ВОЗВРАЩАЕТ версию, пересчитанную сервером
   *  (напр. prebooking_expires_at при смене типа клиента) — её вмерживает стор. */
  putDeal(d: Deal): Promise<Deal>;
  deleteDeal(id: string): Promise<void>;
  /** Тело одного вложения сделки (base64) по ключу; null — вложения нет. */
  getDealAttachment(dealId: string, key: string): Promise<string | null>;
}

// ── API-реализация ─────────────────────────────────────────
const API_BASE = '/api';

/** Общая обработка ответа: 401 → разлогин (вход протух). */
function checkAuth(res: Response): void {
  if (res.status === 401) notifyUnauthorized();
}

async function jget<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: { Accept: 'application/json', ...authHeaders() } });
  checkAuth(res);
  if (!res.ok) throw new Error(`GET ${path} → HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

/** Достаёт человекочитаемый текст ошибки из тела ответа сервера ({error: "..."}). */
async function serverError(res: Response, fallback: string): Promise<Error> {
  try {
    const body = await res.json();
    if (body && typeof body.error === 'string') return new Error(body.error);
  } catch { /* тело не JSON — используем запасной текст */ }
  return new Error(fallback);
}

async function jput<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  checkAuth(res);
  // 413 у Fastify отдаётся с англ. телом «Payload Too Large» — подменяем понятным текстом:
  // пользователь должен понять, что файл слишком большой, а не увидеть техническую строку.
  if (res.status === 413) throw new Error('Файл слишком большой (максимум 50 МБ). Уменьшите файл и попробуйте снова.');
  if (!res.ok) throw await serverError(res, `PUT ${path} → HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

async function jdelete(path: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, { method: 'DELETE', headers: { ...authHeaders() } });
  checkAuth(res);
  if (!res.ok) throw new Error(`DELETE ${path} → HTTP ${res.status}`);
}

class ApiRepo implements Repo {
  readonly mode = 'api' as const;

  async loadAll(): Promise<Snapshot> {
    // Справочники и сделки — параллельно (поведение как раньше: грузит всё).
    const [refs, deals] = await Promise.all([this.loadRefs(), this.loadDeals()]);
    return { ...refs, deals };
  }

  async loadRefs(): Promise<RefSnapshot> {
    const [branches, employees, teams, sources, occasions, game_formats, refusal_reasons] =
      await Promise.all([
        jget<Branch[]>('/branches'),
        jget<Employee[]>('/employees'),
        jget<Team[]>('/teams'),
        jget<Source[]>('/sources'),
        jget<Occasion[]>('/occasions'),
        jget<GameFormat[]>('/game_formats'),
        jget<RefusalReason[]>('/refusal_reasons'),
      ]);
    return { branches, employees, teams, sources, occasions, game_formats, refusal_reasons };
  }

  async loadDeals(from?: number, to?: number): Promise<Deal[]> {
    // ?from=&to= (epoch-ms) — только когда оба заданы и валидны; иначе тянем все сделки.
    let path = '/deals';
    if (Number.isFinite(from) && Number.isFinite(to)) {
      const qs = new URLSearchParams({ from: String(from), to: String(to) });
      path = `/deals?${qs.toString()}`;
    }
    return jget<Deal[]>(path);
  }

  async getSettings(): Promise<AppSettings> {
    const s = await jget<AppSettings | null>('/settings');
    if (s) return s;
    // Первый запуск на сервере — кладём дефолт.
    await jput('/settings', DEFAULT_SETTINGS);
    return DEFAULT_SETTINGS;
  }
  async putSettings(s: AppSettings) { await jput('/settings', s); }

  async putBranch(b: Branch) { await jput(`/branches/${b.id}`, b); }
  async deleteBranch(id: string) { await jdelete(`/branches/${id}`); }
  async putEmployee(e: Employee) { await jput(`/employees/${e.id}`, e); }
  async deleteEmployee(id: string) { await jdelete(`/employees/${id}`); }
  async putTeam(t: Team) { await jput(`/teams/${t.id}`, t); }
  async deleteTeam(id: string) { await jdelete(`/teams/${id}`); }
  // Перед отправкой вырезаем тела уже сохранённых вложений (has_data): их base64 повторно
  // гонять на сервер незачем — это раздувало запрос и тормозило прикрепление. Уходят только
  // новые тела; сервер достраивает недостающие из базы. См. stripSavedAttachmentBodies.
  async putDeal(d: Deal) { return jput<Deal>(`/deals/${d.id}`, stripSavedAttachmentBodies(d)); }
  async deleteDeal(id: string) { await jdelete(`/deals/${id}`); }

  async getDealAttachment(dealId: string, key: string): Promise<string | null> {
    const res = await fetch(`${API_BASE}/deals/${dealId}/attachments?key=${encodeURIComponent(key)}`, {
      headers: { Accept: 'application/json', ...authHeaders() },
    });
    checkAuth(res);
    if (res.status === 404) return null;   // вложение удалили, пока мы его качали
    if (!res.ok) throw new Error(`GET /deals/${dealId}/attachments → HTTP ${res.status}`);
    const body = await res.json() as { data_url?: string };
    return body?.data_url ?? null;
  }
}

// ── Dexie-реализация (локальный демо-режим) ────────────────
class DexieRepo implements Repo {
  readonly mode = 'local' as const;

  async loadAll(): Promise<Snapshot> {
    const [refs, deals] = await Promise.all([this.loadRefs(), this.loadDeals()]);
    return { ...refs, deals };
  }

  async loadRefs(): Promise<RefSnapshot> {
    await seedIfEmpty();
    const [branches, employees, teams, sources, occasions, game_formats, refusal_reasons] =
      await Promise.all([
        db.branches.toArray(),
        db.employees.toArray(),
        db.teams.toArray(),
        db.sources.toArray(),
        db.occasions.toArray(),
        db.game_formats.toArray(),
        db.refusal_reasons.toArray(),
      ]);
    return { branches, employees, teams, sources, occasions, game_formats, refusal_reasons };
  }

  async loadDeals(from?: number, to?: number): Promise<Deal[]> {
    await seedIfEmpty();
    const all = await db.deals.toArray();
    if (!(Number.isFinite(from) && Number.isFinite(to))) return all;
    // Локальный демо-режим: фильтруем по дате игры в памяти (start_at, иначе event_date).
    return all.filter((d) => {
      const ts = d.event?.start_at ?? d.event?.event_date;
      return ts != null && ts >= (from as number) && ts < (to as number);
    });
  }

  async getSettings() { return ensureSettings(); }
  async putSettings(s: AppSettings) { await db.settings.put(s); }

  async putBranch(b: Branch) { await db.branches.put(b); }
  async deleteBranch(id: string) { await db.branches.delete(id); }
  async putEmployee(e: Employee) { await db.employees.put(e); }
  async deleteEmployee(id: string) { await db.employees.delete(id); }
  async putTeam(t: Team) { await db.teams.put(t); }
  async deleteTeam(id: string) { await db.teams.delete(id); }
  async putDeal(d: Deal) { await db.deals.put(d); return d; }
  async deleteDeal(id: string) { await db.deals.delete(id); }

  // Локально сделка лежит целиком, вместе с телами вложений — догружать нечего.
  async getDealAttachment(dealId: string, key: string): Promise<string | null> {
    const d = await db.deals.get(dealId);
    return d ? (collectAttachmentBodies(d)[key] ?? null) : null;
  }
}

// ── Выбор режима ───────────────────────────────────────────
let repoPromise: Promise<Repo> | null = null;

/** Достижим ли backend (GET /api/ping). Короткий таймаут — не вешаем старт. */
async function backendReachable(): Promise<boolean> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch(`${API_BASE}/ping`, { signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return false;
    // ВАЖНО: dev-сервер (vite) отвечает на неизвестные пути index.html со статусом 200.
    // Поэтому недостаточно res.ok — проверяем, что это реально наш JSON-пинг {ok:true}.
    // Иначе локальная разработка без backend ушла бы в API-режим и упёрлась в экран входа.
    const data = await res.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch {
    return false;
  }
}

/** Вернуть репозиторий (один раз выбрав режим). */
export function getRepo(): Promise<Repo> {
  if (repoPromise) return repoPromise;
  repoPromise = (async () => {
    const online = await backendReachable();
    return online ? new ApiRepo() : new DexieRepo();
  })();
  return repoPromise;
}
